import React from "react";
import PropTypes from "prop-types";
import Checkbox from "@mui/material/Checkbox";
import { closeModal, openModal } from "../../redux/modal/modalActions";
import Button from "../common/button/Button";
import { TableLinkCell } from "../common/table/TableDisplayCells";
import {
  TABLE_EMPTY_CELL,
  formatPersonDisplay,
  formatCreatedByDisplay,
  formatPropertyFullAddress,
} from "../../utils/tableDisplayUtils";
import { normalizeTaskType } from "../../helpers/taskUpdateHelper";
import { TaskPriorityChip } from "../common/taskPriority/TaskPriorityChip";
import {
  frequencyLabel,
  subPhaseName,
  taskTypeLabel,
} from "./taskDetailLabels";
import "./PropertyTasksSection.css";

function TaskDetailLongText({ text }) {
  const value = typeof text === "string" ? text.trim() : "";
  if (!value) return TABLE_EMPTY_CELL;
  return <div className="property-tasks-detail-longtext">{text}</div>;
}

function TaskDetailModalContent({
  task,
  dispatch,
  canUpdateTasks,
  canDeleteTasks,
  busyTaskId,
  onEdit,
  onConfirmDelete,
  onStatusCheckbox,
  showPropertyAddress = false,
}) {
  const busy = busyTaskId === task.id;
  const subLabel =
    task.sub_phase != null ? subPhaseName(task.sub_phase) || TABLE_EMPTY_CELL : TABLE_EMPTY_CELL;
  const propertyAddress = formatPropertyFullAddress(task);

  return (
    <div className="property-tasks-mobile-detail">
      <div className="property-tasks-mobile-detail-header">
        <h4 className="property-tasks-mobile-detail-title">Task details</h4>
        <button
          type="button"
          className="property-tasks-mobile-detail-close"
          aria-label="Close"
          onClick={() => dispatch(closeModal())}
        >
          ×
        </button>
      </div>
      <div className="property-tasks-mobile-detail-body">
        <dl className="property-tasks-mobile-detail-dl">
          {showPropertyAddress && propertyAddress !== TABLE_EMPTY_CELL && (
            <div className="property-tasks-mobile-detail-row property-tasks-mobile-detail-row--longtext">
              <dt>Property</dt>
              <dd>
                <TaskDetailLongText text={propertyAddress} />
              </dd>
            </div>
          )}
          <div className="property-tasks-mobile-detail-row property-tasks-mobile-detail-row--longtext">
            <dt>Description</dt>
            <dd>
              <TaskDetailLongText text={task.description} />
            </dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Priority</dt>
            <dd>
              <TaskPriorityChip priority={task.priority} />
            </dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Phase</dt>
            <dd>{task.phase?.name ?? TABLE_EMPTY_CELL}</dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Sub-phase</dt>
            <dd>{subLabel}</dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Type</dt>
            <dd>{taskTypeLabel(task.task_type)}</dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Location</dt>
            <dd>{task.location?.trim() ? task.location : TABLE_EMPTY_CELL}</dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Assigned to</dt>
            <dd>{formatPersonDisplay(task.assigned_to)}</dd>
          </div>
          {normalizeTaskType(task.task_type) === "recurring" && (
            <div className="property-tasks-mobile-detail-row">
              <dt>Frequency</dt>
              <dd>{frequencyLabel(task.frequency)}</dd>
            </div>
          )}
          <div className="property-tasks-mobile-detail-row">
            <dt>Start date</dt>
            <dd>{task.start_date ?? TABLE_EMPTY_CELL}</dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>End date</dt>
            <dd>{task.end_date ?? TABLE_EMPTY_CELL}</dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Days passed</dt>
            <dd>
              {task.days_passed !== undefined && task.days_passed !== null
                ? String(task.days_passed)
                : TABLE_EMPTY_CELL}
            </dd>
          </div>
          <div className="property-tasks-mobile-detail-row property-tasks-mobile-detail-row--status">
            <dt>Completed</dt>
            <dd>
              <Checkbox
                size="small"
                checked={task.status === "completed"}
                disabled={!canUpdateTasks || busy}
                onChange={(e) => onStatusCheckbox(task, e.target.checked)}
                sx={{ p: 0.25 }}
              />
            </dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Link</dt>
            <dd>
              <TableLinkCell
                url={task.link}
                linkClassName="property-tasks-compact-link"
              />
            </dd>
          </div>
          <div className="property-tasks-mobile-detail-row property-tasks-mobile-detail-row--longtext">
            <dt>Comment</dt>
            <dd>
              <TaskDetailLongText text={task.comment} />
            </dd>
          </div>
          <div className="property-tasks-mobile-detail-row">
            <dt>Created by</dt>
            <dd>{formatCreatedByDisplay(task.created_by, TABLE_EMPTY_CELL)}</dd>
          </div>
        </dl>
      </div>
      <div className="property-tasks-mobile-detail-footer">
        {canUpdateTasks && onEdit && (
          <Button
            type="button"
            buttonText="Edit"
            className="btn-login property-tasks-mobile-detail-btn"
            disabled={busy}
            onClick={onEdit}
          />
        )}
        {canDeleteTasks && onConfirmDelete && (
          <Button
            type="button"
            buttonText="Delete"
            className="btn-login property-tasks-mobile-detail-btn"
            disabled={busy}
            onClick={onConfirmDelete}
          />
        )}
        <Button
          type="button"
          buttonText="Close"
          className="btn-login property-tasks-mobile-detail-btn property-tasks-mobile-detail-btn--secondary"
          onClick={() => dispatch(closeModal())}
        />
      </div>
    </div>
  );
}

TaskDetailModalContent.propTypes = {
  task: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  canUpdateTasks: PropTypes.bool,
  canDeleteTasks: PropTypes.bool,
  busyTaskId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onEdit: PropTypes.func,
  onConfirmDelete: PropTypes.func,
  onStatusCheckbox: PropTypes.func.isRequired,
  showPropertyAddress: PropTypes.bool,
};

export function openTaskDetailModal(
  dispatch,
  {
    task,
    canUpdateTasks = false,
    canDeleteTasks = false,
    busyTaskId = null,
    onEdit,
    onConfirmDelete,
    onStatusCheckbox,
    showPropertyAddress = false,
  },
) {
  dispatch(
    openModal(
      <TaskDetailModalContent
        task={task}
        dispatch={dispatch}
        canUpdateTasks={canUpdateTasks}
        canDeleteTasks={canDeleteTasks}
        busyTaskId={busyTaskId}
        onEdit={onEdit}
        onConfirmDelete={onConfirmDelete}
        onStatusCheckbox={onStatusCheckbox}
        showPropertyAddress={showPropertyAddress}
      />,
    ),
  );
}

export default TaskDetailModalContent;
